import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { AmplifyHttpClient } from '../auth/http.amplify-httpclient';
import { TaskResponse } from '../../models/task';
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { loaderTypes } from '@app/shared/component/loader/loader';

@Injectable({
  providedIn: 'root',
})
export class TaskService {
  private API_TASK_URL = 'api/task';

  private readonly taskChanges$ = new Subject<string>();

  constructor(private readonly httpService: AmplifyHttpClient) {}

  getTaskId(request): Observable<any> {
    return this.httpService.post<any>(`${this.API_TASK_URL}/new-task-id`, request).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getTaskDetails(userId, taskId): Observable<any> {
    return this.httpService.get<any>(`${this.API_TASK_URL}/task/${userId}/${taskId}`, null, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  createTask(request): Observable<TaskResponse> {
    return this.httpService.post<TaskResponse>(`${this.API_TASK_URL}/task`, request).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  updateTask(request): Observable<TaskResponse> {
    return this.httpService.patch<TaskResponse>(`${this.API_TASK_URL}/task`, request).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  getNotLinkedAccountItems(id): Observable<any> {
    return this.httpService.get<any>(`api/vault/form-not-linked-account/${id}`, null, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status === 200) {
          return response.data;
        } else {
          return response;
        }
      })
    );
  }

  createUploadUrl(request): Observable<any> {
    return this.httpService.post<any>(`${this.API_TASK_URL}/create-attachment-upload-url`, request).pipe(
      map((response: any) => {
        if (response.status == 200 || response.status == 201) {
          if (response?.data?.limit_exceeded) {
            let error: HttpErrorResponse = new HttpErrorResponse({ error: response?.data, status: 400 });
            throw error;
          } else {
            return response.data;
          }
        } else {
          return response;
        }
      })
    );
  }

  updateUploadStatus(request): Observable<any> {
    return this.httpService.patch<any>(`${this.API_TASK_URL}/attachment-upload`, request, loaderTypes.none).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  deleteUploadedAttachment(userId, id): Observable<any> {
    return this.httpService.delete<any>(`${this.API_TASK_URL}/attachment-upload/${userId}/${id}`).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  downloadAttachment(userId, fileId): Observable<any> {
    return this.httpService.get<any>(`${this.API_TASK_URL}/attachment-download-url/${userId}/${fileId}`).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  getTaskList(userId, folderId): Observable<any> {
    return this.httpService.get<any>(`${this.API_TASK_URL}/tasks-vault-folder/${userId}/${folderId}`).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  updateTaskDetails(request): Observable<any> {
    return this.httpService.post<any>(`${this.API_TASK_URL}/task-bulk-update`, request).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  getTaskListOfAllRelatedFolder(userId, folderId): Observable<any> {
    return this.httpService.get<any>(`${this.API_TASK_URL}/tasks-vault-folder/${userId}/${folderId}/all`).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  deleteTaskForever(userId, taskId): Observable<any> {
    return this.httpService.delete<any>(`${this.API_TASK_URL}/task/${userId}/${taskId}`).pipe(
      map((response: any) => {
        if (response.status == 200) {
          return response;
        } else {
          return response;
        }
      })
    );
  }

  taskUpdated(text: string): void {
    this.taskChanges$.next(text);
  }

  taskChanges(): Observable<string> {
    return this.taskChanges$.asObservable();
  }
}
